// 🔥 디바이스 카탈로그 훅 - 최고급 React 훅 구현
// [배경] Firebase 디바이스 카탈로그를 React 컴포넌트에서 효율적으로 사용하기 위한 커스텀 훅
//        실시간 업데이트, 캐싱, 에러 처리, 로딩 상태 관리 모든 기능 포함

import { useState, useEffect, useCallback, useMemo } from 'react';
import { getDeviceCatalogService } from '../services/deviceCatalogAdapter';
import { 
  DeviceCatalogDocument, 
  DeviceSearchOptions,
  DeviceType,
  DeviceBrand 
} from '../types/deviceCatalog';

// 🔄 훅 상태 인터페이스
interface UseDeviceCatalogState {
  models: DeviceCatalogDocument[];
  loading: boolean;
  error: string | null;
  lastUpdated: Date | null;
}

interface UseDeviceCatalogOptions {
  autoFetch?: boolean;
  cacheEnabled?: boolean;
  refreshInterval?: number;
}

/**
 * 🎯 카테고리별 디바이스 모델 조회 훅
 */
export const useDevicesByCategory = (
  category: DeviceType | null,
  options: UseDeviceCatalogOptions = {}
) => {
  const { autoFetch = true, cacheEnabled = true, refreshInterval } = options;
  
  const [state, setState] = useState<UseDeviceCatalogState>({
    models: [],
    loading: false,
    error: null,
    lastUpdated: null
  });
  
  // 📡 데이터 조회 함수
  const fetchModels = useCallback(async () => {
    if (!category) return;
    
    setState(prev => ({ ...prev, loading: true, error: null }));
    
    // 🧪 디버깅
    console.log(`🔍 useDevicesByCategory 훅 호출 - 카테고리: ${category}`);
    
    try {
      // 🔥 어댑터 서비스 사용
      const CatalogService = getDeviceCatalogService();
      console.log(`🔧 사용 중인 서비스:`, CatalogService.name);
      
      const models = await CatalogService.getModelsByCategory(
        category,
        { useCache: cacheEnabled }
      );
      
      console.log(`✅ 모델 조회 완료 (${category}): ${models.length}개`);
      
      setState({
        models,
        loading: false,
        error: null,
        lastUpdated: new Date()
      });
      
    } catch (error) {
      console.error(`❌ 모델 조회 실패 (${category}):`, error);
      setState(prev => ({
        ...prev,
        loading: false,
        error: error instanceof Error ? error.message : String(error)
      }));
    }
  }, [category, cacheEnabled]);
  
  // 🔄 자동 새로고침 (선택적)
  useEffect(() => {
    if (!refreshInterval) return;
    
    const interval = setInterval(fetchModels, refreshInterval);
    return () => clearInterval(interval);
  }, [fetchModels, refreshInterval]);
  
  // 🚀 초기 데이터 로드
  useEffect(() => {
    if (autoFetch && category) {
      fetchModels();
    }
  }, [autoFetch, category, fetchModels]);
  
  // 📊 파생 데이터 (메모이제이션)
  const derivedData = useMemo(() => ({
    totalCount: state.models.length,
    recommendedModels: state.models.filter(m => m.recommended),
    modelsByBrand: state.models.reduce((acc, model) => {
      acc[model.brand] = (acc[model.brand] || 0) + 1;
      return acc;
    }, {} as Record<DeviceBrand, number>),
    latestModels: state.models
      .filter(m => m.releaseYear >= new Date().getFullYear() - 1)
      .sort((a, b) => (b.releaseYear || 0) - (a.releaseYear || 0))
  }), [state.models]);
  
  return {
    ...state,
    ...derivedData,
    refresh: fetchModels,
    clearError: () => setState(prev => ({ ...prev, error: null }))
  };
};

/**
 * 🎯 브랜드+카테고리별 디바이스 모델 조회 훅
 */
export const useDevicesByBrandAndCategory = (
  brand: DeviceBrand | null,
  category: DeviceType | null,
  options: UseDeviceCatalogOptions = {}
) => {
  const { autoFetch = true, cacheEnabled = true } = options;
  
  const [state, setState] = useState<UseDeviceCatalogState>({
    models: [],
    loading: false,
    error: null,
    lastUpdated: null
  });
  
  const fetchModels = useCallback(async () => {
    if (!brand || !category) return;
    
    setState(prev => ({ ...prev, loading: true, error: null }));
    
    // 🧪 디버깅
    console.log(`🔍 useDevicesByBrandAndCategory 훅 호출 - 브랜드: ${brand}, 카테고리: ${category}`);
    
    try {
      // 🔥 어댑터 서비스 사용
      const CatalogService = getDeviceCatalogService();
      
      const models = await CatalogService.getModelsByBrandAndCategory(
        brand,
        category,
        { useCache: cacheEnabled }
      );
      
      console.log(`✅ 브랜드+카테고리 모델 조회 완료 (${brand} ${category}): ${models.length}개`);
      
      setState({
        models,
        loading: false,
        error: null,
        lastUpdated: new Date()
      });
      
    } catch (error) {
      console.error(`❌ 브랜드+카테고리 모델 조회 실패 (${brand} ${category}):`, error);
      setState(prev => ({
        ...prev,
        loading: false,
        error: error instanceof Error ? error.message : String(error)
      }));
    }
  }, [brand, category, cacheEnabled]);
  
  useEffect(() => {
    if (autoFetch && brand && category) {
      fetchModels();
    }
  }, [autoFetch, brand, category, fetchModels]);
  
  // 📊 브랜드별 파생 데이터
  const derivedData = useMemo(() => ({
    seriesGroups: state.models.reduce((acc, model) => {
      const series = model.series;
      if (!acc[series]) acc[series] = [];
      acc[series].push(model);
      return acc;
    }, {} as Record<string, DeviceCatalogDocument[]>),
    
    storageOptionsAll: Array.from(new Set(
      state.models.flatMap(m => m.storageOptions)
    )).sort((a, b) => {
      const aNum = parseInt(a.replace(/[^0-9]/g, ''));
      const bNum = parseInt(b.replace(/[^0-9]/g, ''));
      return aNum - bNum;
    }),
    
    connectivityOptionsAll: Array.from(new Set(
      state.models.flatMap(m => m.connectivityOptions || [])
    ))
  }), [state.models]);
  
  return {
    ...state,
    ...derivedData,
    refresh: fetchModels,
    clearError: () => setState(prev => ({ ...prev, error: null }))
  };
};

/**
 * 🔍 디바이스 검색 훅 (고급 검색 기능)
 */
export const useDeviceSearch = () => {
  const [state, setState] = useState<UseDeviceCatalogState & {
    searchQuery: string;
    searchOptions: DeviceSearchOptions;
  }>({
    models: [],
    loading: false,
    error: null,
    lastUpdated: null,
    searchQuery: '',
    searchOptions: {}
  });
  
  // 🔍 검색 실행
  const search = useCallback(async (
    query: string, 
    options: DeviceSearchOptions = {}
  ) => {
    setState(prev => ({ 
      ...prev, 
      loading: true, 
      error: null, 
      searchQuery: query,
      searchOptions: options 
    }));
    
    try {
      const models = await DeviceCatalogService.searchModels({
        ...options,
        searchQuery: query
      });
      
      setState(prev => ({
        ...prev,
        models,
        loading: false,
        lastUpdated: new Date()
      }));
      
    } catch (error) {
      setState(prev => ({
        ...prev,
        loading: false,
        error: error instanceof Error ? error.message : String(error)
      }));
    }
  }, []);
  
  // 🔄 검색어 초기화
  const clearSearch = useCallback(() => {
    setState(prev => ({
      ...prev,
      models: [],
      searchQuery: '',
      searchOptions: {},
      error: null
    }));
  }, []);
  
  return {
    ...state,
    search,
    clearSearch,
    clearError: () => setState(prev => ({ ...prev, error: null }))
  };
};

/**
 * 🎯 시리즈별 디바이스 모델 조회 훅
 */
export const useDevicesBySeries = (series: string | null) => {
  const [state, setState] = useState<UseDeviceCatalogState>({
    models: [],
    loading: false,
    error: null,
    lastUpdated: null
  });
  
  const fetchModels = useCallback(async () => {
    if (!series) return;
    
    setState(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      const models = await DeviceCatalogService.getModelsBySeries(series);
      
      setState({
        models,
        loading: false,
        error: null,
        lastUpdated: new Date()
      });
      
    } catch (error) {
      setState(prev => ({
        ...prev,
        loading: false,
        error: error instanceof Error ? error.message : String(error)
      }));
    }
  }, [series]);
  
  useEffect(() => {
    if (series) {
      fetchModels();
    }
  }, [series, fetchModels]);
  
  return {
    ...state,
    refresh: fetchModels,
    clearError: () => setState(prev => ({ ...prev, error: null }))
  };
};

/**
 * 📊 디바이스 통계 훅
 */
export const useDeviceStatistics = () => {
  const [stats, setStats] = useState<{
    totalModels: number;
    byCategory: Record<DeviceType, number>;
    byBrand: Record<DeviceBrand, number>;
    recommendedCount: number;
    loading: boolean;
    error: string | null;
    lastUpdated: Date | null;
  }>({
    totalModels: 0,
    byCategory: { smartphone: 0, tablet: 0, wearable: 0 },
    byBrand: { Apple: 0, Samsung: 0 },
    recommendedCount: 0,
    loading: false,
    error: null,
    lastUpdated: null
  });
  
  const fetchStats = useCallback(async () => {
    setStats(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      const statistics = await DeviceCatalogService.getStatistics();
      
      setStats(prev => ({
        ...prev,
        ...statistics,
        loading: false,
        lastUpdated: new Date()
      }));
      
    } catch (error) {
      setStats(prev => ({
        ...prev,
        loading: false,
        error: error instanceof Error ? error.message : String(error)
      }));
    }
  }, []);
  
  useEffect(() => {
    fetchStats();
  }, [fetchStats]);
  
  return {
    ...stats,
    refresh: fetchStats,
    clearError: () => setStats(prev => ({ ...prev, error: null }))
  };
};
